export type EngagementLeverId =
  | "design-changes"
  | "specification-optimization"
  | "substitution"
  | "standardization"
  | "should-cost"
  | "component-sourcing"
  | "transition";

export interface EngagementLeverMatch {
  id: EngagementLeverId;
  label: string;
  diagnostic: boolean;
  score: number;
}

interface LeverDefinition extends Omit<EngagementLeverMatch, "score"> {
  aliases: readonly string[];
}

// Display hints only: these must never classify savings or change the saved taxonomy.
const LEVERS: readonly LeverDefinition[] = [
  {
    id: "design-changes", label: "Design Changes", diagnostic: false,
    aliases: ["design changes", "product redesign", "redesign", "design improvement", "design simplification", "value engineering"],
  },
  {
    id: "specification-optimization", label: "Specification Optimization", diagnostic: false,
    aliases: ["specification optimization", "spec optimization", "packaging optimization", "specification improvement", "specification reduction", "spec rationalization", "specifications"],
  },
  {
    id: "substitution", label: "Substitution", diagnostic: false,
    aliases: ["substitution", "substitute", "material replacement", "material swap", "alternative materials"],
  },
  {
    id: "standardization", label: "Standardization", diagnostic: false,
    aliases: ["standardization", "standardisation", "harmonization", "harmonisation", "commonization", "variant reduction", "parts commonality"],
  },
  {
    id: "should-cost", label: "Should Cost", diagnostic: true,
    aliases: ["should cost", "shouldcost", "cost modeling", "cost modelling", "cost breakdown", "clean sheet", "cleansheet", "cost diagnostic"],
  },
  {
    id: "component-sourcing", label: "Component Sourcing", diagnostic: false,
    aliases: ["component sourcing", "parts sourcing", "supplier sourcing", "supplier consolidation", "sourcing", "procurement"],
  },
  {
    id: "transition", label: "Transition", diagnostic: false,
    aliases: ["transition", "migration", "relocation", "reshoring", "production transfer"],
  },
];

// Multi-word hints can be loose; a lone word needs stronger evidence. Near ties
// stay unclassified instead of arbitrarily assigning one of several levers.
const PHRASE_THRESHOLD = 0.55;
const TOKEN_THRESHOLD = 0.55;
const SINGLE_WORD_THRESHOLD = 0.82;
const SPECIFIC_ANCHOR_THRESHOLD = 0.8;
const AMBIGUITY_MARGIN = 0.08;
const GENERIC_ANCHORS = new Set(["material", "materials", "product", "parts", "supplier", "component", "packaging", "production"]);

function words(value: string): string[] {
  return value.slice(0, 512).normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().match(/[a-z0-9]+/g)?.slice(0, 64)
    .map((word) => word === "pckg" ? "packaging" : word.slice(0, 64)) ?? [];
}

const TOKENIZED_LEVERS = LEVERS.map(({ aliases, ...lever }) => ({
  ...lever,
  aliases: aliases.map(words),
}));

/** Normalized edit similarity, including adjacent transpositions such as desgin. */
function similarity(left: string, right: string): number {
  if (left === right) return 1;
  if (Math.min(left.length, right.length) < 4) return 0;
  const distance = Array.from({ length: left.length + 1 }, (_, row) =>
    Array.from({ length: right.length + 1 }, (_, column) => row === 0 ? column : column === 0 ? row : 0));
  for (let row = 1; row <= left.length; row++) {
    for (let column = 1; column <= right.length; column++) {
      distance[row][column] = Math.min(
        distance[row - 1][column] + 1,
        distance[row][column - 1] + 1,
        distance[row - 1][column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1),
      );
      if (row > 1 && column > 1 && left[row - 1] === right[column - 2] && left[row - 2] === right[column - 1]) {
        distance[row][column] = Math.min(distance[row][column], distance[row - 2][column - 2] + 1);
      }
    }
  }
  return 1 - distance[left.length][right.length] / Math.max(left.length, right.length);
}

function aliasScore(
  aliasWords: readonly string[],
  nameWords: readonly string[],
  tokenSimilarity: (left: string, right: string) => number,
): number {
  const used = new Set<number>();
  let total = 0;
  let hasSpecificAnchor = false;
  for (const word of aliasWords) {
    let bestScore = 0;
    let bestIndex = -1;
    nameWords.forEach((candidate, index) => {
      if (used.has(index)) return;
      const score = tokenSimilarity(word, candidate);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });
    if (bestScore < (aliasWords.length === 1 ? SINGLE_WORD_THRESHOLD : TOKEN_THRESHOLD)) return 0;
    used.add(bestIndex);
    total += bestScore;
    if (word.length >= 6 && !GENERIC_ANCHORS.has(word) && bestScore >= SPECIFIC_ANCHOR_THRESHOLD) {
      hasSpecificAnchor = true;
    }
  }
  // Exact phrases (including short "should cost") are valid. Fuzzy phrases
  // need a distinctive long token: "packaging" alone cannot anchor a lever.
  if (aliasWords.length > 1 && total < aliasWords.length && !hasSpecificAnchor) return 0;
  return total / aliasWords.length;
}

/** Infer an icon hint, not an authoritative lever. Explicit hints take precedence. */
export function matchEngagementLever(name: string, explicitLever?: string | null): EngagementLeverMatch | null {
  const nameWords = words(explicitLever?.trim() || name);
  if (!nameWords.length) return null;
  // Aliases share words such as "specification" and "cost". Compute each pair
  // once per title, then release it; no global cache retains engagement names.
  const tokenScores = new Map<string, number>();
  const tokenSimilarity = (left: string, right: string): number => {
    const key = `${left}:${right}`;
    const cached = tokenScores.get(key);
    if (cached !== undefined) return cached;
    const score = similarity(left, right);
    tokenScores.set(key, score);
    return score;
  };
  const candidates = TOKENIZED_LEVERS.map(({ aliases, ...lever }) => ({
    ...lever,
    score: Math.max(...aliases.map((alias) => aliasScore(alias, nameWords, tokenSimilarity))),
  })).sort((left, right) => right.score - left.score);
  const best = candidates[0];
  if (best.score < PHRASE_THRESHOLD || best.score - candidates[1].score < AMBIGUITY_MARGIN) return null;
  return best;
}
